import React, { useState } from 'react';
import LegalLayout from '../components/LegalLayout';

const PrivacyPolicy = () => {
  // Date courante pour les besoins de démonstration (à remplacer par la vraie date actuelle)
  const today = new Date();
  
  // Versions historiques des politiques avec leur contenu spécifique
  const policyVersions = [
    { 
      date: new Date('2025-01-15'), 
      notes: "Création initiale de la politique",
      content: {
        introduction: "Version initiale de notre politique de confidentialité.",
        sections: [
          {
            title: "Version initiale",
            content: "Cette version a été créée le 15 janvier 2025. Elle contient les informations de base sur notre politique de confidentialité."
          }
        ]
      }
    },
    { 
      date: new Date('2025-02-20'), 
      notes: "Mise à jour concernant les cookies",
      content: {
        introduction: "Mise à jour de notre politique concernant l'utilisation des cookies.",
        sections: [
          {
            title: "Mise à jour cookies",
            content: "Cette mise à jour du 20 février 2025 précise notre utilisation des cookies et technologies similaires."
          }
        ]
      }
    },
    { 
      date: new Date('2025-03-18'), 
      notes: "Mise en conformité avec la Loi 25 du Québec",
      content: {
        introduction: "Révisions pour assurer la conformité avec la nouvelle législation québécoise sur la protection des données personnelles.",
        sections: [
          {
            title: "Conformité Loi 25",
            content: "Les modifications du 18 mars 2025 visent à garantir notre conformité avec la nouvelle législation québécoise."
          }
        ]
      }
    },
    { 
      date: today, 
      notes: "Version actuelle - Ajout du préavis de 30 jours pour toute modification future",
      content: {
        introduction: "Cette version est la politique de confidentialité complète et actuelle, incluant l'engagement formel de notifier les utilisateurs 30 jours avant toute modification significative.",
        sections: [
          {
            title: "Version consolidée avec préavis",
            content: "Politique de confidentialité à jour, consolidant toutes les révisions antérieures et ajoutant l'engagement de préavis de 30 jours."
          }
        ]
      }
    }
  ];

  const [selectedDate, setSelectedDate] = useState(today);

  // Trouver la version de politique correspondant à la date sélectionnée
  const selectedVersion = policyVersions.find(version => 
    version.date.getTime() === selectedDate.getTime()
  ) || policyVersions[policyVersions.length - 1];

  // Obtenir la date formatée
  const formattedDate = selectedDate.toLocaleDateString('fr-FR', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  // Fonction pour vérifier si une date est aujourd'hui
  const isToday = (date) => {
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  return (
    <LegalLayout 
      title="Politique de Confidentialité"
      lastUpdated={formattedDate}
    >
      <div className="space-y-8">
        {/* Sélecteur de version */}
        <section className="mb-6">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="font-medium text-gray-800 mb-2">Historique des modifications</h3>
            <div className="flex flex-wrap gap-2">
              {policyVersions.map((version, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedDate(version.date)}
                  className={`px-3 py-1 rounded-md text-sm ${
                    selectedDate.getTime() === version.date.getTime()
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  {isToday(version.date) 
                    ? "Aujourd'hui" 
                    : version.date.toLocaleDateString('fr-FR', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })
                  }
                </button>
              ))}
            </div>
            <p className="mt-2 text-sm text-gray-600">
              {selectedVersion.notes}
            </p>
          </div>
        </section>

        {/* Si c'est une ancienne version, afficher un avertissement */}
        {!isToday(selectedDate) && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <p className="text-amber-700">
              <strong>Note:</strong> Vous consultez une version historique de notre politique de confidentialité. 
              <button 
                onClick={() => setSelectedDate(today)} 
                className="ml-2 text-blue-600 hover:text-blue-800 underline"
              >
                Voir la version actuelle
              </button>
            </p>
          </div>
        )}

        {/* Contenu spécifique à la version sélectionnée */}
        <section>
          <p className="text-base sm:text-lg text-gray-600">
            {selectedVersion.content.introduction}
          </p>
        </section>

        {/* Sections de la version sélectionnée */}
        {selectedVersion.content.sections.map((section, index) => (
          <section key={index}>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              {section.title}
            </h2>
            <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100">
              <p className="text-gray-600">{section.content}</p>
            </div>
          </section>
        ))}

        {/* Afficher le contenu complet seulement pour la version actuelle */}
        {isToday(selectedDate) && (
          <>
            {/* Déclaration de transparence */}
            <section className="mb-6">
              <div className="bg-yellow-50 p-6 rounded-xl border border-yellow-200">
                <h3 className="font-medium text-gray-800 mb-2">Déclaration de transparence</h3>
                <p className="text-gray-700">
                  Clicdroits.info est géré par un entrepreneur individuel. Par souci de transparence, nous tenons à préciser que:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-gray-700 mt-2">
                  <li>Nous dépendons de fournisseurs tiers (Microsoft, Google, Vercel) pour l'hébergement et les services associés</li>
                  <li>Nos capacités en matière de sécurité et de traitement des données sont limitées par les outils fournis par ces prestataires</li>
                  <li>En cas d'incident chez l'un de ces fournisseurs, notre capacité de réponse sera nécessairement impactée</li>
                  <li>Nous nous efforçons d'être conformes à la Loi 25 dans la mesure de nos moyens raisonnables</li>
                  <li>Nous traitons les demandes d'accès, de rectification ou de suppression manuellement, ce qui peut prendre jusqu'à 30 jours</li>
                </ul>
              </div>
            </section>

            {/* Responsible person information */}
            <section className="mb-6">
              <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100">
                <h3 className="font-medium text-gray-800 mb-2">Responsable de la protection des renseignements personnels</h3>
                <p className="text-gray-600">
                  Conformément à la Loi 25 du Québec, nous désignons formellement <strong>Abderauf Kakhlaui</strong>, fondateur de Clicdroits.info, 
                  comme responsable de la protection des renseignements personnels (RPRP). Pour toute question ou demande concernant 
                  vos renseignements personnels, vous pouvez le contacter directement à l'adresse : <a href="mailto:raouf@clicdroits.info" className="text-primary hover:text-primary/80 underline">
                    raouf@clicdroits.info
                  </a>
                </p>
              </div>
            </section>

            <section>
              <p className="text-base sm:text-lg text-gray-600">
                Chez Clicdroits.info, nous accordons une importance capitale à la protection de vos données personnelles. 
                Cette politique explique comment nous collectons, utilisons et protégeons vos informations 
                lorsque vous utilisez notre site.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Politiques et pratiques de gouvernance
              </h2>
              <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100">
                <p className="text-gray-600 mb-3">
                  En tant qu'entrepreneur individuel, notre approche de la gouvernance est nécessairement simplifiée mais conforme aux principes de la Loi 25 du Québec:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-gray-600">
                  <li>Les demandes d'accès et de rectification sont traitées directement par le responsable par email</li>
                  <li>Un dossier de suivi simple est tenu pour documenter les demandes reçues et les mesures prises</li>
                  <li>Les renseignements personnels sont conservés uniquement sur des services sécurisés fournis par nos prestataires</li>
                  <li>Une revue périodique des pratiques est effectuée pour assurer la conformité continue</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Données collectées
              </h2>
              <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100">
                <p className="mb-2 font-medium text-gray-600">Nous pouvons collecter les types d'informations suivants :</p>
                <ul className="list-disc pl-5 space-y-2 text-gray-600">
                  <li>Adresse courriel uniquement dans la section de feedback</li>
                  <li>Informations de connexion et données techniques (adresse IP, type de navigateur)</li>
                  <li>Informations que vous nous fournissez volontairement lors de l'utilisation de nos services, en veillant à ne pas inclure de données permettant de vous identifier personnellement</li>
                  <li>Données de navigation via des cookies et technologies similaires</li>
                </ul>
                <p className="mt-3 italic text-gray-600">
                  Nous vous demandons expressément de ne pas vous identifier ou partager des informations personnelles dans vos messages afin de préserver votre anonymat et d'éviter les conflits d'intérêt.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Traitement des adresses courriel de feedback
              </h2>
              <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100">
                <p className="text-gray-600 mb-3">
                  Lorsque vous nous fournissez votre adresse courriel dans la section de feedback:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-gray-600">
                  <li>Votre adresse est stockée sur notre compte Microsoft Outlook</li>
                  <li>Nous la conservons généralement pour une durée maximale de 12 mois</li>
                  <li>Le traitement est simple et manuel - nous ne disposons pas de systèmes automatisés complexes</li>
                  <li>Pour demander l'accès, la rectification ou la suppression de votre adresse courriel, envoyez simplement un email à notre RPRP</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Utilisation de Microsoft Office/Outlook
              </h2>
              <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100">
                <p className="text-gray-600 mb-3">
                  Nous utilisons les services Microsoft Office, incluant Outlook, pour notre correspondance par courriel. Il est important de comprendre que:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-gray-600">
                  <li>Les courriels que vous nous envoyez sont traités et stockés sur les serveurs de Microsoft</li>
                  <li>Nous dépendons entièrement des mesures de sécurité mises en place par Microsoft</li>
                  <li>En cas d'incident affectant Microsoft, nos données pourraient être compromises malgré nos meilleures intentions</li>
                  <li>Nous n'avons pas de contrôle direct sur l'infrastructure de Microsoft, mais nous avons choisi ce service pour son niveau de sécurité reconnu</li>
                  <li>Nous utilisons un compte Microsoft professionnel avec <strong>authentification à deux facteurs obligatoire</strong> pour limiter significativement les risques d'accès non autorisé</li>
                  <li>L'accès à nos comptes Microsoft est strictement limité et surveillé</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Utilisation des données
              </h2>
              <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100">
                <p className="mb-2 text-gray-600">Les informations collectées nous permettent de :</p>
                <ul className="list-disc pl-5 space-y-2 text-gray-600">
                  <li>Fournir, maintenir et améliorer nos services</li>
                  <li>Personnaliser votre expérience utilisateur</li>
                  <li>Communiquer avec vous concernant votre compte ou nos services</li>
                  <li>Vous envoyer des informations techniques, juridiques ou administratives importantes</li>
                  <li>Détecter et prévenir les fraudes et autres activités abusives</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Cookies et technologies similaires
              </h2>
              <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100">
                <p className="text-gray-600 mb-2">
                  Notre site utilise des cookies et technologies similaires pour améliorer l'expérience utilisateur, 
                  analyser l'utilisation du site et personnaliser le contenu.
                </p>
                <p className="text-gray-600 mb-3">
                  Conformément à la Loi 25 du Québec, nous avons classé nos cookies par catégorie :
                </p>
                
                <div className="bg-white p-4 rounded-lg border border-blue-100 mb-4">
                  <h4 className="font-medium text-gray-800 mb-2">Cookies essentiels (toujours actifs)</h4>
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="p-2 text-left">Nom</th>
                        <th className="p-2 text-left">Finalité</th>
                        <th className="p-2 text-left">Durée</th>
                        <th className="p-2 text-left">Provenance</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t">
                        <td className="p-2">session_id</td>
                        <td className="p-2">Maintien de la session utilisateur</td>
                        <td className="p-2">Session</td>
                        <td className="p-2">Interne</td>
                      </tr>
                      <tr className="border-t">
                        <td className="p-2">csrf_token</td>
                        <td className="p-2">Protection contre les attaques CSRF</td>
                        <td className="p-2">Session</td>
                        <td className="p-2">Interne</td>
                      </tr>
                      <tr className="border-t">
                        <td className="p-2">cookie_consent</td>
                        <td className="p-2">Enregistre vos préférences sur les cookies</td>
                        <td className="p-2">1 an</td>
                        <td className="p-2">Interne</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                <div className="bg-white p-4 rounded-lg border border-blue-100 mb-4">
                  <h4 className="font-medium text-gray-800 mb-2">Cookies analytiques (nécessitent votre consentement)</h4>
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="p-2 text-left">Nom</th>
                        <th className="p-2 text-left">Finalité</th>
                        <th className="p-2 text-left">Durée</th>
                        <th className="p-2 text-left">Provenance</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t">
                        <td className="p-2">_ga</td>
                        <td className="p-2">Google Analytics - Distingue les utilisateurs</td>
                        <td className="p-2">2 ans</td>
                        <td className="p-2">Google</td>
                      </tr>
                      <tr className="border-t">
                        <td className="p-2">_ga_*</td>
                        <td className="p-2">Google Analytics - Maintient l'état de la session</td>
                        <td className="p-2">2 ans</td>
                        <td className="p-2">Google</td>
                      </tr>
                      <tr className="border-t">
                        <td className="p-2">__vercel_analytics</td>
                        <td className="p-2">Statistiques d'utilisation de Vercel</td>
                        <td className="p-2">1 an</td>
                        <td className="p-2">Vercel</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                <div className="bg-white p-4 rounded-lg border border-blue-100 mb-4">
                  <h4 className="font-medium text-gray-800 mb-2">Cookies de personnalisation (nécessitent votre consentement)</h4>
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="p-2 text-left">Nom</th>
                        <th className="p-2 text-left">Finalité</th>
                        <th className="p-2 text-left">Durée</th>
                        <th className="p-2 text-left">Provenance</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t">
                        <td className="p-2">preferred_language</td>
                        <td className="p-2">Sauvegarde vos préférences linguistiques</td>
                        <td className="p-2">1 an</td>
                        <td className="p-2">Interne</td>
                      </tr>
                      <tr className="border-t">
                        <td className="p-2">display_mode</td>
                        <td className="p-2">Sauvegarde votre préférence de thème (clair/sombre)</td>
                        <td className="p-2">1 an</td>
                        <td className="p-2">Interne</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                <p className="text-gray-600 mt-3 mb-3">
                  Nous utilisons une bannière de consentement simple fournie par notre hébergeur. Les cookies non essentiels sont désactivés par défaut.
                  Notre bannière n'est pas un système sophistiqué, mais elle vous permet de faire un choix conforme à la Loi 25.
                </p>
                
                <p className="text-gray-600">
                  Vous pouvez également configurer votre navigateur pour bloquer tous les cookies si vous préférez.
                  Notez que le refus des cookies non essentiels peut affecter certaines fonctionnalités de notre site.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Prise de décision automatisée et profilage
              </h2>
              <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100">
                <p className="text-gray-600">
                  Clicdroits.info n'utilise pas de technologies pour prendre des décisions basées exclusivement sur un traitement automatisé.
                  Notre assistant juridique virtuel (Clicobot) ne fait que fournir des informations générales et ne conserve pas de données
                  vous identifiant personnellement.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Sécurité des données
              </h2>
              <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100">
                <p className="text-gray-600 mb-3">
                  En tant qu'entrepreneur individuel, notre approche de la sécurité s'appuie principalement sur:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-gray-600">
                  <li>L'utilisation de services tiers réputés (Microsoft, Google, Vercel) pour l'hébergement et le traitement des données</li>
                  <li><strong>L'authentification à deux facteurs (2FA) obligatoire pour tous les comptes administratifs</strong>, ce qui empêche l'accès non autorisé même en cas de compromission d'un mot de passe</li>
                  <li>Des mots de passe forts et uniques pour chaque service, gérés par un gestionnaire de mots de passe sécurisé</li>
                  <li>Des mises à jour régulières de notre site pour corriger les vulnérabilités</li>
                  <li><strong>Une infrastructure distribuée</strong> - notre site ne repose pas sur un seul ordinateur, mais sur une infrastructure cloud professionnnelle via Vercel, offrant une bien meilleure sécurité et fiabilité qu'un serveur local unique</li>
                  <li>Des sauvegardes régulières et chiffrées du code source et des configurations</li>
                </ul>
                <p className="text-gray-600 mt-3">
                  Ces mesures de sécurité nous permettent, même en tant qu'entrepreneur individuel, d'assurer un niveau de protection significatif pour vos données. Malgré ces précautions, nous ne pouvons pas garantir une sécurité absolue car nous dépendons également de nos fournisseurs.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Conservation des données
              </h2>
              <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100">
                <p className="text-gray-600 mb-3">
                  Nous conservons vos données personnelles aussi longtemps que nécessaire pour fournir nos services 
                  et répondre aux exigences légales. Voici nos périodes de conservation approximatives:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-gray-600">
                  <li>Adresses courriel de feedback : environ 12 mois après la dernière interaction</li>
                  <li>Données de navigation : selon les paramètres par défaut de Google Analytics (généralement 26 mois)</li>
                  <li>Journaux de connexion : environ 90 jours, gérés principalement par notre hébergeur Vercel</li>
                </ul>
                <p className="mt-3 text-gray-600">
                  La suppression des données est réalisée manuellement sur demande. Nous ne disposons pas de systèmes automatisés 
                  de purge des données, mais nous nous efforçons de respecter ces délais dans la mesure du possible.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Partage des données
              </h2>
              <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100">
                <p className="mb-2 text-gray-600">Nous pouvons partager vos informations avec :</p>
                <ul className="list-disc pl-5 space-y-2 text-gray-600">
                  <li>Nos prestataires de services qui nous aident à exploiter notre plateforme</li>
                  <li>Des autorités légales si nous y sommes contraints par la loi</li>
                  <li>Des tiers en cas de restructuration, fusion ou vente</li>
                </ul>
                <p className="mt-3 text-gray-600">
                  Nous ne vendons pas vos données personnelles à des tiers pour leur propre usage marketing.
                </p>
                <p className="mt-3 text-gray-600">
                  Bien que nous n'ayons pas de contrats sophistiqués avec nos prestataires, nous utilisons les services
                  standards de Microsoft, Google et Vercel qui incluent leurs propres conditions de protection des données.
                  En tant qu'entrepreneur individuel, nous ne sommes pas en mesure de négocier des contrats personnalisés
                  avec ces grandes entreprises.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Dépendance vis-à-vis des tiers
              </h2>
              <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100">
                <p className="text-gray-600 mb-3">
                  Par souci de transparence, il est important de comprendre que:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-gray-600">
                  <li>Nous dépendons entièrement de Microsoft pour la sécurité des courriels</li>
                  <li>Nous dépendons de Vercel pour l'hébergement et la sécurité de notre site web</li>
                  <li>Nous dépendons de Google pour les statistiques d'utilisation</li>
                  <li>Si l'un de ces fournisseurs subit une violation de données, nos services pourraient être affectés</li>
                  <li>Nous avons choisi ces fournisseurs pour leur réputation en matière de sécurité, mais nous n'avons pas 
                  la capacité d'évaluer indépendamment toutes leurs mesures</li>
                  <li>L'utilisation d'une <strong>infrastructure cloud moderne</strong> via Vercel nous permet de bénéficier d'une protection bien supérieure à ce qu'un serveur local unique pourrait offrir</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Évaluations de sécurité et conformité
              </h2>
              <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100">
                <p className="text-gray-600 mb-3">
                  Nous nous appuyons sur les certifications publiées par nos fournisseurs de services. Ces liens sont fournis
                  à titre informatif, mais nous n'avons pas la capacité de vérifier indépendamment ces certifications:
                </p>
                
                <div className="bg-white p-4 rounded-lg border border-blue-100 mb-4">
                  <h4 className="font-medium text-gray-800 mb-2">Microsoft Office/Outlook</h4>
                  <ul className="list-disc pl-5 space-y-2 text-gray-600">
                    <li>
                      <a href="https://learn.microsoft.com/fr-ca/compliance/regulatory/offering-ccpa" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 underline">
                        Centre de conformité Microsoft - Loi sur la protection des renseignements personnels au Canada
                      </a>
                    </li>
                    <li>
                      <a href="https://servicetrust.microsoft.com/fr-fr/compliance/iso-iec-27001" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 underline">
                        Certification ISO 27001 (Gestion de la sécurité de l'information)
                      </a>
                    </li>
                  </ul>
                </div>
                
                <div className="bg-white p-4 rounded-lg border border-blue-100 mb-4">
                  <h4 className="font-medium text-gray-800 mb-2">Google Analytics</h4>
                  <ul className="list-disc pl-5 space-y-2 text-gray-600">
                    <li>
                      <a href="https://support.google.com/analytics/answer/9019185" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 underline">
                        Principes de confidentialité de Google Analytics
                      </a>
                    </li>
                    <li>
                      <a href="https://support.google.com/analytics/answer/6004245" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 underline">
                        Contrôles de confidentialité dans Google Analytics
                      </a>
                    </li>
                  </ul>
                </div>
                
                <div className="bg-white p-4 rounded-lg border border-blue-100 mb-4">
                  <h4 className="font-medium text-gray-800 mb-2">Vercel (Hébergement)</h4>
                  <ul className="list-disc pl-5 space-y-2 text-gray-600">
                    <li>
                      <a href="https://vercel.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 underline">
                        Politique de confidentialité de Vercel
                      </a>
                    </li>
                    <li>
                      <a href="https://vercel.com/security" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 underline">
                        Pratiques de sécurité de Vercel
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Transfert de données hors Québec
              </h2>
              <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100">
                <p className="text-gray-600 mb-3">
                  Nos fournisseurs de services (Microsoft, Google, Vercel) peuvent stocker des données à l'extérieur du Québec.
                  La Loi 25 exige une évaluation des facteurs relatifs à la vie privée (ÉFVP) avant tout transfert.
                </p>
                <p className="text-gray-600 mb-3">
                  En toute transparence, en tant qu'entrepreneur individuel, je ne dispose pas des ressources pour mener
                  une ÉFVP complète et formelle. Cependant:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-gray-600">
                  <li>J'ai vérifié que ces entreprises sont conformes au RGPD européen, souvent considéré comme plus strict que la Loi 25</li>
                  <li>J'ai constaté que Microsoft propose des options d'hébergement au Canada qui peuvent limiter les transferts internationaux</li>
                  <li>Je limite au maximum les données personnelles collectées pour réduire les risques</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Incidents de confidentialité
              </h2>
              <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100">
                <p className="text-gray-600 mb-3">
                  En cas d'incident de confidentialité affectant vos renseignements personnels:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-gray-600">
                  <li>Je serai probablement informé par le fournisseur concerné (Microsoft, Google ou Vercel)</li>
                  <li>Je noterai l'incident dans un simple registre</li>
                  <li>Si l'incident présente un risque sérieux, je vous contacterai personnellement par email</li>
                  <li>Je notifierai la Commission d'accès à l'information du Québec (CAI) si nécessaire</li>
                </ul>
                <p className="text-gray-600 mt-3">
                  N'étant pas une grande entreprise avec des systèmes de surveillance sophistiqués, ma capacité à détecter
                  des incidents de manière indépendante est limitée. Je dépends en grande partie des notifications de mes fournisseurs.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Vos droits
              </h2>
              <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100">
                <p className="mb-2 text-gray-600">Conformément à la Loi 25, vous disposez des droits suivants:</p>
                <ul className="list-disc pl-5 space-y-2 text-gray-600">
                  <li><strong>Droit d'accès</strong> : Obtenir confirmation que nous traitons vos données et en recevoir une copie</li>
                  <li><strong>Droit de rectification</strong> : Faire corriger des données inexactes</li>
                  <li><strong>Droit à l'effacement</strong> : Demander la suppression de vos données</li>
                  <li><strong>Droit à la portabilité</strong> : Recevoir vos données dans un format réutilisable</li>
                  <li><strong>Droit d'opposition</strong> : Vous opposer au traitement de vos données</li>
                </ul>
                <p className="mt-3 text-gray-600">
                  Pour exercer ces droits, envoyez simplement un email à raouf@clicdroits.info en précisant clairement votre demande.
                  Je la traiterai manuellement dans un délai maximal de 30 jours. Étant un entrepreneur individuel, je n'ai pas
                  de processus automatisé pour ces demandes, mais je m'engage à répondre à chacune d'elles personnellement.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Modifications de la politique
              </h2>
              <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100">
                <p className="text-gray-600 mb-3">
                  Nous pouvons être amenés à mettre à jour cette politique périodiquement pour refléter des changements dans nos pratiques 
                  ou pour des raisons légales. La date de la dernière mise à jour est indiquée en haut de cette page.
                </p>
                <div className="bg-amber-50 p-4 rounded-lg border border-amber-100 mt-4">
                  <h3 className="font-medium text-amber-800 mb-2 flex items-center">
                    <span className="mr-2">⚠️</span> Engagement de préavis
                  </h3>
                  <p className="text-amber-700">
                    <strong>Nous nous engageons formellement</strong> à vous informer au moins 30 jours avant l'entrée en vigueur de toute modification substantielle 
                    de notre politique de confidentialité. Cet avis sera clairement affiché sur notre site et comprendra:
                  </p>
                  <ul className="list-disc pl-5 space-y-2 text-amber-700 mt-2">
                    <li>Un résumé des changements prévus</li>
                    <li>La date d'entrée en vigueur des modifications</li>
                    <li>Vos options concernant vos données</li>
                  </ul>
                  <p className="text-amber-700 mt-2">
                    Ce délai de 30 jours vous permettra de prendre connaissance des changements et de décider si vous souhaitez continuer 
                    à utiliser nos services ou prendre des mesures concernant vos données personnelles.
                  </p>
                </div>
                <p className="text-gray-600 mt-4">
                  Les modifications mineures ou techniques pourront être effectuées sans préavis spécifique, mais seront toujours documentées 
                  dans l'historique des versions accessible depuis cette page.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Me contacter
              </h2>
              <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100">
                <p className="text-gray-600">
                  Pour toute question concernant cette politique de confidentialité ou la manière dont je traite vos données, 
                  veuillez me contacter directement:
                </p>
                <div className="mt-3 p-4 bg-white rounded-lg border border-blue-100">
                  <p className="font-medium text-gray-800">Abderauf Kakhlaui</p>
                  <p className="text-gray-600">Fondateur et responsable de la protection des renseignements personnels</p>
                  <p className="text-gray-600">
                    Email : <a href="mailto:raouf@clicdroits.info" className="text-primary hover:text-primary/80 underline">
                      raouf@clicdroits.info
                    </a>
                  </p>
                </div>
                <p className="mt-3 text-gray-600">
                  Vous pouvez également contacter la Commission d'accès à l'information du Québec pour toute question ou plainte :
                  <br />
                  <a href="https://www.cai.gouv.qc.ca" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 underline">
                    www.cai.gouv.qc.ca
                  </a>
                </p>
              </div>
            </section>
          </>
        )}
      </div>
    </LegalLayout>
  );
};

export default PrivacyPolicy;
